/**
 * Browser speechSynthesis (TTS) for reading clarification questions aloud.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function isSpeechTtsSupported() {
  return typeof window !== 'undefined' && !!window.speechSynthesis;
}

export default function useSpeechTTS({ lang = 'en-IN' } = {}) {
  const [supported] = useState(() => isSpeechTtsSupported());
  const [enabled, setEnabled] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);

  const cancel = useCallback(() => {
    if (utterRef.current) {
      utterRef.current.onstart = null;
      utterRef.current.onend = null;
      utterRef.current.onerror = null;
    }
    utterRef.current = null;
    try {
      window.speechSynthesis?.cancel?.();
    } catch {
      // ignore
    }
    setSpeaking(false);
  }, []);

  useEffect(() => () => cancel(), [cancel]);

  const speak = useCallback(
    (text) => {
      const trimmed = String(text || '').trim();
      if (!supported || !enabled || !trimmed) return;
      cancel();
      const utter = new SpeechSynthesisUtterance(trimmed);
      utter.lang = lang || 'en-IN';
      utter.rate = 1;
      utter.onstart = () => {
        if (utterRef.current === utter) setSpeaking(true);
      };
      utter.onend = () => {
        if (utterRef.current !== utter) return;
        setSpeaking(false);
        utterRef.current = null;
      };
      utter.onerror = () => {
        if (utterRef.current !== utter) return;
        setSpeaking(false);
        utterRef.current = null;
      };
      utterRef.current = utter;
      try {
        window.speechSynthesis.speak(utter);
      } catch {
        setSpeaking(false);
      }
    },
    [supported, enabled, lang, cancel]
  );

  const toggleEnabled = useCallback(() => {
    setEnabled((prev) => {
      if (prev) cancel();
      return !prev;
    });
  }, [cancel]);

  return {
    supported,
    enabled,
    speaking,
    speak,
    cancel,
    toggleEnabled,
    setEnabled
  };
}
